import React from 'react'
import i18next from 'i18next'
import { FormControls } from '../sharedLayout/forms'
import { Link } from '../../utils/link'
import { cookiebarClasses, cookiePlaceholderClasses } from '../../utils/style'
import { isEmbedded } from '../../utils/embed'
import { getClassName } from '../../utils/utils'
import { Accepted, Cookie, requiredCookies } from '../../utils/cookies'

export type CookiePlaceholderStyle = Partial<{
  cookiesClassName: string
  cookiesInnerClassName: string
  cookiesTextClassName: string
  cookiesClickableClassName: string
  cookiesClickableLabelClassName: string
  cookiesClickableIconClassName: string
  iconId: string
}>

export type CookiebarStyle = Partial<{
  cookiebarClassName: string
  cookiebarOuterClassName: string
  cookiebarInnerClassName: string
  cookiebarTextClassName: string
  cookiebarTitleClassName: string
  cookiebarIntroClassName: string
  cookiebarFooterClassName: string
  cookiebarClickableWrapperClassName: string
  cookiebarClickablePrimaryClassName: string
  cookiebarClickableSecondaryClassName: string
  cookiebarClickableLabelClassName: string
  cookiebarClickableIconClassName: string
  iconId: string
  cookiebarFormClassName: string
  cookiebarFormListClassName: string
  cookiebarFormItemClassName: string
  cookiebarExpandClassName: string
  cookiebarExpandLabelClassName: string
  cookiebarExpandIconClassName: string
  expandIconId: string
}>

export const CookiebarLayout = (props: {
  showOptions: boolean
  toggleOptions: () => void
  onAcceptAll: () => void
  onDeclineAll: () => void
  onApplySettings: () => void
  onSetSingle: (cookie: Cookie, state: Accepted) => void
  selectedCookieValues: Map<Cookie, Accepted>
}) => {
  const {
    showOptions,
    toggleOptions,
    onAcceptAll,
    onDeclineAll,
    onApplySettings,
    selectedCookieValues: cookieValues,
    onSetSingle,
  } = props

  return (
    <section
      id="cookiebar"
      className={getClassName(cookiebarClasses, 'cookiebarClassName', 'text-center bg-light')}
      role="alert"
      aria-labelledby="a11y-cookiebar-title"
    >
      <div className={getClassName(cookiebarClasses, 'cookiebarOuterClassName', 'py-5')}>
        <div className={getClassName(cookiebarClasses, 'cookiebarInnerClassName', '')}>
          <div className={getClassName(cookiebarClasses, 'cookiebarTextClassName', '')}>
            <h2 id="a11y-cookiebar-title" className={getClassName(cookiebarClasses, 'cookiebarTitleClassName', 'h3')}>
              {i18next.t('cookies:cookiebarTitle', {
                siteName: 'Spaarne Gasthuis',
              })}
            </h2>
            <div
              className={getClassName(cookiebarClasses, 'cookiebarIntroClassName', '')}
              dangerouslySetInnerHTML={{
                __html: `${i18next.t('cookies:cookiebarIntro')} <a href="${i18next.t(
                  'footer:legalCookieStatementUrl'
                )}" rel="noopener noreferrer" target="_blank">${i18next.t('footer:legalCookieStatementText')}</a>.`,
              }}
            />
          </div>
          <div className={getClassName(cookiebarClasses, 'cookiebarFooterClassName', '')}>
            <div className={getClassName(cookiebarClasses, 'cookiebarClickableWrapperClassName', '')}>
              <button
                className={getClassName(cookiebarClasses, 'cookiebarClickablePrimaryClassName', 'btn btn-dark')}
                onClick={onAcceptAll}
              >
                <span className={getClassName(cookiebarClasses, 'cookiebarClickableLabelClassName', '')}>
                  {i18next.t('cookies:cookiebarAcceptCookies')}
                </span>
                {/* {SVGIcon && (
                  <svg
                    className={getClassName(
                      options,
                      'cookiebarClickableIconClassName',
                      'ml-3'
                    )}
                    aria-hidden="true"
                    width={SVGIcon.width ? SVGIcon.width : '24px'}
                    height={SVGIcon.height ? SVGIcon.height : '24px'}
                  >
                    <use
                      xlinkHref={getClassName(
                        options,
                        'iconId',
                        '#icon--chevron-down'
                      )}
                    />
                  </svg>
                )} */}
              </button>
            </div>
            <div className={getClassName(cookiebarClasses, 'cookiebarClickableWrapperClassName', '')}>
              <button
                className={getClassName(cookiebarClasses, 'cookiebarClickableSecondaryClassName', 'btn btn-dark')}
                onClick={onDeclineAll}
              >
                <span className={getClassName(cookiebarClasses, 'cookiebarClickableLabelClassName', '')}>
                  {i18next.t('cookies:cookiebarDeclineCookies')}
                </span>
                {/* {SVGIcon && (
                  <svg
                    className={getClassName(
                      options,
                      'cookiebarClickableIconClassName',
                      'ml-3'
                    )}
                    aria-hidden="true"
                    width={SVGIcon.width ? SVGIcon.width : '24px'}
                    height={SVGIcon.height ? SVGIcon.height : '24px'}
                  >
                    <use
                      xlinkHref={getClassName(
                        options,
                        'iconId',
                        '#icon--chevron-right-large'
                      )}
                    />
                  </svg>
                )} */}
              </button>
            </div>
          </div>

          <div className={getClassName(cookiebarClasses, 'cookiebarFormClassName', '')}>
            {/* Expand button */}
            <button
              className={getClassName(cookiebarClasses, 'cookiebarExpandClassName', 'btn btn-dark')}
              onClick={toggleOptions}
              aria-controls="a11y-cookiebar-toggle"
              aria-expanded={showOptions}
            >
              <span className={getClassName(cookiebarClasses, 'cookiebarExpandLabelClassName', '')}>
                {i18next.t('cookies:cookiebarShowOptions')}
              </span>
              <svg
                className={getClassName(cookiebarClasses, 'cookiebarExpandIconClassName', 'ml-3')}
                aria-hidden="true"
                width="14px"
                height="14px"
              >
                <use xlinkHref={getClassName(cookiebarClasses, 'expandIconId', '#icon--chevron-down')} />
              </svg>
            </button>

            {/* Expand options */}
            <div
              id="a11y-cookiebar-toggle"
              aria-hidden={!showOptions}
              className={getClassName(cookiebarClasses, 'cookiebarFormListClassName', '')}
            >
              <ul>
                {Array.from(cookieValues).map((cookieValue) => {
                  const cookie = cookieValue[0]
                  const isAccepted = cookieValue[1]
                  return (
                    <li className={getClassName(cookiebarClasses, 'cookiebarFormItemClassName', '')} key={cookie}>
                      {/* Bootstrap checkboxes */}
                      <FormControls.FormCheckInGroup
                        controlId={cookie}
                        label={
                          <>
                            {i18next.t(
                              cookie === 'functional-cookies'
                                ? 'cookies:cookiebarFunctional'
                                : cookie === 'analytical-cookies'
                                ? 'cookies:cookiebarAnalytical'
                                : cookie === 'video-cookies'
                                ? 'cookies:cookiebarVideos'
                                : cookie === 'tracking-cookies'
                                ? 'cookies:cookiebarTracking'
                                : 'cookies:cookiebarReadspeaker'
                            )}
                            {'. '}
                            {i18next.t(
                              cookie === 'functional-cookies'
                                ? 'cookies:cookiebarFunctionalCookies'
                                : cookie === 'analytical-cookies'
                                ? 'cookies:cookiebarAnalyticalCookies'
                                : cookie === 'video-cookies'
                                ? 'cookies:cookiebarVideosCookies'
                                : cookie === 'tracking-cookies'
                                ? 'cookies:cookiebarTrackingCookies'
                                : 'cookies:cookiebarReadspeakerCookies'
                            )}
                          </>
                        }
                        readOnly={requiredCookies.includes(cookie)}
                        required={requiredCookies.includes(cookie)}
                        checked={isAccepted === 'yes'}
                        onChange={(newValue) => {
                          onSetSingle(cookie, newValue ? 'yes' : 'no')
                        }}
                      />
                    </li>
                  )
                })}
              </ul>

              <button
                className={getClassName(cookiebarClasses, 'cookiebarClickablePrimaryClassName', 'btn btn-dark')}
                onClick={onApplySettings}
              >
                <span className={getClassName(cookiebarClasses, 'cookiebarClickableLabelClassName', '')}>
                  {i18next.t('cookies:cookiebarChangeSettings')}
                </span>
                {/* <svg
                  className={getClassName(cookiebarClasses, 'cookiebarClickableIconClassName', 'ml-3')}
                  aria-hidden="true"
                  width="24px"
                  height="24px"
                >
                  <use xlinkHref={getClassName(cookiebarClasses, 'iconId', '#icon--chevron-right-large')} />
                </svg> */}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export const CookiePlaceholderLayout = (props: { onAccept: () => void }) => {
  const { onAccept } = props
  // In a third-party iframe nothing may write a cookie (SSG-442), so the consent
  // button and the link to the cookie settings are both dropped: the button would
  // write a cookie the browser blocks anyway, and the settings page is not
  // reachable from inside the frame. The explanatory text is kept so the reader
  // can see that something was withheld.
  const embedded = isEmbedded()
  return (
    <div className={getClassName(cookiePlaceholderClasses, 'cookiesClassName', 'text-center bg-light')}>
      <div className={getClassName(cookiePlaceholderClasses, 'cookiesInnerClassName', 'py-5')}>
        <div className={getClassName(cookiePlaceholderClasses, 'cookiesTextClassName', '')}>
          {i18next.t('cookies:cookiePlaceholderText')}
          {!embedded && (
            <>
              {' '}
              <Link url={i18next.t('cookies:cookiePlaceholderLinkToCookies')}>
                {i18next.t('cookies:cookiePlaceholderLinkLabel')}
              </Link>
            </>
          )}
          .
        </div>
        {!embedded && (
          <button
            className={getClassName(cookiePlaceholderClasses, 'cookiesClickableClassName', 'btn btn-dark')}
            onClick={onAccept}
          >
            <span className={getClassName(cookiePlaceholderClasses, 'cookiesClickableLabelClassName', '')}>
              {i18next.t('cookies:cookiebarAcceptCookies')}
            </span>
            {/* <svg
              className={getClassName(cookiePlaceholderClasses, 'cookiesClickableIconClassName', 'ml-3')}
              aria-hidden="true"
              width="24px"
              height="24px"
            >
              <use xlinkHref={getClassName(cookiePlaceholderClasses, 'iconId', '#icon--chevron-right-large')} />
            </svg> */}
          </button>
        )}
      </div>
    </div>
  )
}
